/**
 * Customer registry for TPO Gen config resolution.
 *
 * Selects which static `fe_config` module (and, for the dev-fallback path, which
 * dev BE-fallback fixture) to use, keyed by customer. `fetchRawConfig` (see
 * `hooks/queries/useCustomerConfig.ts`) consults this on BOTH branches: the
 * `shouldUseFallbackConfig()` dev-fallback branch pairs the fixture + fe_config,
 * and the real-backend branch pairs the BE response + fe_config - so the active
 * customer's `fe_config` (field rules, metric rules, cascade rules, etc.) is
 * always used regardless of which branch runs.
 *
 * Switch customer with the `REACT_APP_TPO_GEN_CUSTOMER` env var (falls back to
 * the app-wide `REACT_APP_TENANT_ID`, then to `gp`). Run, e.g.:
 *   REACT_APP_TPO_GEN_USE_FALLBACK_CONFIG=true \
 *   REACT_APP_CUSTOMER_ENV=dev \
 *   REACT_APP_TPO_GEN_CUSTOMER=danone \
 *   npm start
 *
 * Add a future customer by creating its `<customer>TpoGenConfig.ts` +
 * `fallback/fallback<Customer>Config.json` and registering a CUSTOMER_REGISTRY entry.
 */

type ConfigModuleLoader = () => Promise<{ default: unknown }>;

interface CustomerConfigSources {
    /** Loads the static fe_config module (default export). */
    feConfig: ConfigModuleLoader;
    /** Loads the dev BE-fallback fixture (default export). */
    fallbackFixture: ConfigModuleLoader;
}

// Static dynamic-import thunks so webpack code-splits each customer's config.
export const CUSTOMER_REGISTRY: Record<string, CustomerConfigSources> = {
    gp: {
        feConfig: () => import('./tpoGenConfig'),
        fallbackFixture: () => import('./fallback/fallbackCustomerConfig.json'),
    },
    naturesbakery: {
        feConfig: () => import('./danoneTpoGenConfig'),
        fallbackFixture: () => import('./fallback/fallbackDanoneConfig.json'),
    },
    danone: {
        feConfig: () => import('./danoneTpoGenConfig'),
        fallbackFixture: () => import('./fallback/fallbackDanoneConfig.json'),
    },
    demo: {
        feConfig: () => import('./danoneTpoGenConfig'),
        fallbackFixture: () => import('./fallback/fallbackDanoneConfig.json'),
    },
};

export type CustomerKey = keyof typeof CUSTOMER_REGISTRY;

const DEFAULT_CUSTOMER: CustomerKey = 'gp';

/**
 * Resolves the active customer key from env, coerced to a known registry key.
 * Honours `REACT_APP_TPO_GEN_CUSTOMER`, then the app-wide `REACT_APP_TENANT_ID`,
 * then defaults to `gp`. An unknown value falls back to `gp`.
 */
export const resolveActiveCustomer = (): CustomerKey => {
    const requested = (process.env.REACT_APP_TPO_GEN_CUSTOMER || process.env.REACT_APP_TENANT_ID || '').toLowerCase();
    return Object.prototype.hasOwnProperty.call(CUSTOMER_REGISTRY, requested)
        ? (requested as CustomerKey)
        : DEFAULT_CUSTOMER;
};

/** Config sources (fe_config + fallback fixture) for the active customer. */
export const getActiveCustomerSources = (): CustomerConfigSources => CUSTOMER_REGISTRY[resolveActiveCustomer()];
